import { autoinject } from "aurelia-framework";
import { StandardLoadOptions } from "app/shared/lib/essette-site";
import { IApiActionResults, IApiQueryResults, IApiBatchProgress } from 'app/shared/lib/api/api-types';
import * as api from 'app/shared/lib/essette-api';
import * as UM from 'app/um/models/essette-api';

@autoinject
export class AdmissionCareDayService {

    ////****************** Fields and Properties ************************************////

    ////****************** Element References ***************************************////

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(public api: api.Api) { }

    ////****************** Functions ************************************************////

    ////****************** Queries **************************************************////
    @api.apiCall
    public async loadCareDays(authId: string, options?: StandardLoadOptions, includes: string[] = [], fields: string[] = []): Promise<api.ApiQueryResponse<UM.AdmissionCareDay>> {
        const query = new api.ApiQuery('um/admissions/caredays')
            .filterString('auth.Id', authId)
            .applyStandardLoadOptions(options, { applyPaginationSizeToDetails: true })
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    @api.apiCall
    public async loadCareDayStatusHistory(careDayId: string, options?: StandardLoadOptions, includes: string[] = [], fields: string[] = []): Promise<api.ApiQueryResponse<UM.AdmissionCareDayStatusHistory>> {
        const query = new api.ApiQuery('um/admissions/caredays/history/statuses')
            .filterString('careDay.Id', careDayId)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadCareDayStatuses(options?: StandardLoadOptions, includes: string[] = [], withReasons: boolean = false): Promise<IApiQueryResults<UM.AdmissionCareDayStatus>> {
        const query = new api.ApiQuery('um/admissions/caredays/statuses')
            .applyStandardLoadOptions(options);

        if(withReasons) {
            query.includeWithState(options, 'reasons')
                .filterExists('reasons');

            if(!includes.includes('reasons')) {
                includes.push('reasons');
            }
        }

        query.include(...includes);

        return await this.api.get(query);
    }

    /**
     * Apply multiple days in one api call
     * @param days array of care days
     * @returns array of results
     */
    public async applyCareDayBatch(days: UM.AdmissionCareDay[], progress?: IApiBatchProgress): Promise<IApiActionResults[]> {
        const batch = this.api.batch();
        for(const d of days) {
            if(!d.id)
                batch.post(this.api.action('insert', 'um/admissions/caredays', d));
            else
                batch.post(this.api.action('update', `um/admissions/caredays/${d.id}`, d))
        }
        return await this.api.execute(batch, { progress });
    }
    @api.apiCall
    public async insertCareDay(day: UM.AdmissionCareDay): Promise<api.ApiPostResponse> {
        const action = new api.ApiAction('insert', `um/admissions/caredays`, day);

        return await this.api.post(action);
    }

    @api.apiCall
    public async updateCareDay(day: UM.AdmissionCareDay): Promise<api.ApiPostResponse> {
        const action = new api.ApiAction('update', `um/admissions/caredays/${day.id}`, day);

        return await this.api.post(action);
    }

    public async activateCareDay(careDayId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `um/admissions/caredays/${careDayId}`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async activateCareDayStatus(careDayStatusId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `um/admissions/caredays/statuses/${careDayStatusId}`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async activateCareDayStatusReason(careDayStatusReasonId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `um/admissions/caredays/statuses/reasons/${careDayStatusReasonId}`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    @api.apiCall
    public async updateStatusHistory(statusHistory: UM.AdmissionCareDayStatusHistory): Promise<api.ApiPostResponse> {
        const action = new api.ApiAction('update', `um/admissions/caredays/history/statuses/${statusHistory.id}`, statusHistory);

        return await this.api.post(action);
    }

    @api.apiCall
    public async loadStatusTransitions(statusId: string, options?: StandardLoadOptions, includes: string[] = [], fields: string[] = []): Promise<api.ApiQueryResponse<UM.AdmissionCareDayStatus>> {
        const query = new api.ApiQuery('um/admissions/caredays/statuses')
            .filterString('id', statusId)
            .applyStandardLoadOptions(options, { applyPaginationSizeToDetails: true })
            .field(...fields);

        includes.forEach(i => { query.include(i) });

        return await this.api.get(query);
    }

    @api.apiCall
    public async loadInitialStatus(includes: string[] = [], fields: string[] = []): Promise<api.ApiQueryResponse<UM.AdmissionCareDayStatus>> {
        const query = new api.ApiQuery('um/admissions/caredays/statuses')
            .filterString('flag.isInitialStatus', 'true')
            .field(...fields);

        includes.forEach(i => { query.include(i) });

        return await this.api.get(query);
    }

    @api.apiCall
    public async loadStatusReasons(statusId: string, options?: StandardLoadOptions, includes: string[] = [], fields: string[] = []): Promise<api.ApiQueryResponse<UM.AuthStatus>> {
        const query = new api.ApiQuery('um/admissions/caredays/statuses/reasons')
            .filterString('status.Id', statusId)
            .applyStandardLoadOptions(options)
            .field(...fields);

        includes.forEach(i => { query.include(i) });

        return await this.api.get(query);
    }

    @api.apiCall
    public async loadLevelsOfCare(options?: StandardLoadOptions, includes: string[] = [], fields: string[] = []): Promise<api.ApiQueryResponse<UM.LevelOfCare>> {
        const query = new api.ApiQuery('um/levelsofcare')
            .applyStandardLoadOptions(options)
            .field(...fields);

        includes.forEach(i => { query.include(i) });

        return await this.api.get(query);
    }

    @api.apiCall
    public async loadDischargeLocations(options?: StandardLoadOptions, includes: string[] = [], fields: string[] = []): Promise<api.ApiQueryResponse<UM.DischargeLocation>> {
        const query = new api.ApiQuery('um/auths/admissions/dischargeLocations')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    ////****************** Actions **************************************************////
    @api.apiCall
    public async saveDischargeLocation(dischargeLocation: UM.DischargeLocation): Promise<api.ApiPostResponse> {
        const actionName = !dischargeLocation.id ? 'insert' : 'update';
        const url = !dischargeLocation.id ? `um/auths/admissions/dischargeLocations/` : `um/auths/admissions/dischargeLocations/${dischargeLocation.id}`;
        const action = new api.ApiAction(actionName, url, dischargeLocation);

        return await this.api.post(action);
    }

    public async saveCareDayStatus(careDayStatus: UM.AdmissionCareDayStatus): Promise<IApiActionResults> {
        const actionName = !careDayStatus.id ? 'insert' : 'update';
        const url = !careDayStatus.id ? `um/admissions/careDays/statuses` : `um/admissions/careDays/statuses/${careDayStatus.id}`;
        const action = new api.ApiAction(actionName, url, careDayStatus);

        return await this.api.post(action);
    }

    public async saveCareDayStatusReason(statusReason: UM.AdmissionCareDayStatusReason): Promise<IApiActionResults> {
        const actionName = !statusReason.id ? 'insert' : 'update';
        const url = `um/admissions/caredays/statuses/reasons/${statusReason.id ? `${statusReason.id}` : ``}`;
        const action = this.api.action(actionName, url, statusReason);
        return await this.api.post(action);
    }

    public async updateCareDayStatusExclusive(careDayStatusId: string): Promise<IApiActionResults> {
        const url = `um/admissions/careDays/statuses/${careDayStatusId}`;
        const action = new api.ApiAction('updateExclusive', url, { flag: { isInitialStatus: true } });

        return await this.api.post(action);
    }

    @api.apiCall
    public async activateDischargeLocation(dischargeLocationId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const url = `um/auths/admissions/dischargeLocations/${dischargeLocationId}`;
        const action = this.api.action(actionCode, url, { isActive: isActive })

        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    @api.apiCall
    public async activateLevelOfCare(levelOfCareId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const url = `um/levelsOfCare/${levelOfCareId}`;
        const action = this.api.action(actionCode, url, { isActive: isActive })

        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }


    @api.apiCall
    public async loadProviderLocation(options?: StandardLoadOptions, includes: string[] = [], withLevelsOfCare: boolean = false): Promise<IApiQueryResults<UM.ProviderLocation>> {
        const query = new api.ApiQuery('um/providerlocations')
            .applyStandardLoadOptions(options);

        if(withLevelsOfCare) {
            query.includeWithState(options, 'levelsofcare')
                .filterExists('levelsofcare');

            if(!includes.includes('levelsofcare')) {
                includes.push('levelsofcare');
            }
        }

        query.include(...includes);

        return await this.api.get(query);
    }

    @api.apiCall
    public async loadProviderLocationByIdWithLevelsOfCare(providerLocationId: string, options?: StandardLoadOptions, includes: string[] = []): Promise<IApiQueryResults<UM.ProviderLocation>> {

        if(!includes.includes('levelsofcare')) {
            includes.push('levelsofcare');
        }

        const query = new api.ApiQuery('um/providerlocations')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .includeWithState(options, 'levelsofcare')
            .filterExists('levelsofcare')
            .filterString('id', providerLocationId);

        return await this.api.get(query);
    }

    public async deleteLevelsOfCareDetail(levelOfCare: UM.LevelOfCare): Promise<IApiActionResults> {
        const action = this.api.action('deleteDetails', `um/levelsofcare/${levelOfCare.id}`, levelOfCare);
        return await this.api.post(action);
    }

    public async insertLevelsOfCareDetail(levelOfCare: UM.LevelOfCare): Promise<IApiActionResults> {
        const action = this.api.action('insertDetails', `um/levelsofcare/${levelOfCare.id}`, levelOfCare);
        return await this.api.post(action);
    }

    public async updateLevelsOfCareDetail(levelOfCare: UM.LevelOfCare): Promise<IApiActionResults> {
        const action = this.api.action('updateDetail', `um/levelsofcare/${levelOfCare.id}`, levelOfCare);
        return await this.api.post(action);
    }

    @api.apiCall
    public async saveLevelOfCare(levelOfCare: UM.LevelOfCare): Promise<IApiActionResults> {
        const actionName = !levelOfCare.id ? 'insert' : 'update';
        const url = !levelOfCare.id ? `um/levelsOfCare` : `um/levelsOfCare/${levelOfCare.id}`;
        const action = new api.ApiAction(actionName, url, levelOfCare);

        return await this.api.post(action);
    }
}
